import { Box, Stack } from 'grommet';
import { CirclePlay } from 'grommet-icons';
import { PropsWithChildren, useMemo } from 'react';
import ReactPlayer from 'react-player';
import { borderRadiusSize } from 'src/theme';

export type TalonVideoPlayerProps = {
  onError?: (error: Error) => void;
  url?: string;
  controls?: boolean;
  preview?: boolean;
  width?: string;
  height?: string;
  thumbnailOnly?: boolean;
  onThumbnailClick?: () => void;
  onClearAsset?: () => void;
};

export const TalonVideoPlayer = ({
  url,
  preview = false,
  controls = true,
  width,
  height,
  thumbnailOnly = false,
  onThumbnailClick,
  onError,
}: TalonVideoPlayerProps) => {
  /** Computed **/
  const videoPlayer = useMemo(() => {
    if (!url) {
      return <></>;
    }
    // if the URL points to google drive, embed the google video player
    else if (url.startsWith('https://drive.google.com/')) {
      const videoId = url.split('/d/')[1]?.split('/')[0];
      if (!videoId) {
        onError && onError(new Error('Invalid Google Drive URL'));
        return <></>;
      }
      const googleDriveUrl = `https://drive.google.com/file/d/${videoId}/preview`;

      return (
        <iframe
          src={googleDriveUrl}
          width={width || '100%'}
          height={height || '100%'}
          allow="autoplay"
          title="Google Drive Video Player"
        />
      );
    } else if (ReactPlayer.canPlay && ReactPlayer.canPlay(url)) {
      return (
        <ReactPlayer
          src={url}
          controls={controls}
          width={width || '100%'}
          height={height || '100%'}
          light={preview}
          style={{
            maxHeight: '500px',
            maxWidth: '100%',
            borderBottomLeftRadius: borderRadiusSize,
            borderBottomRightRadius: borderRadiusSize,
          }}
        />
      );
    } else {
      onError && onError(new Error('Unsupported video format'));
      return <></>;
    }
  }, [url, onError, width, height, ReactPlayer.canPlay, controls, preview]);

  /** Render **/

  const VideoPlayerContainer = ({ children }: PropsWithChildren) =>
    thumbnailOnly ? (
      <Stack>
        {children}
        <Box align="center" justify="center" fill onClick={onThumbnailClick}>
          <CirclePlay size="large" color="white" />
        </Box>
      </Stack>
    ) : (
      <Box fill style={{ cursor: 'pointer' }}>
        {children}
      </Box>
    );

  return <VideoPlayerContainer>{videoPlayer}</VideoPlayerContainer>;
};
